import { geoAlbers, geoConicEqualArea, GeoStream } from "d3-geo";

const epsilon = 1e-6;

function multiplex(streams: GeoStream[]): GeoStream {
  const n = streams.length;
  return {
    point(x: number, y: number) { for (let i = 0; i < n; i++) streams[i].point(x, y); },
    sphere() { for (let i = 0; i < n; i++) streams[i].sphere?.(); },
    lineStart() { for (let i = 0; i < n; i++) streams[i].lineStart(); },
    lineEnd() { for (let i = 0; i < n; i++) streams[i].lineEnd(); },
    polygonStart() { for (let i = 0; i < n; i++) streams[i].polygonStart(); },
    polygonEnd() { for (let i = 0; i < n; i++) streams[i].polygonEnd(); },
  };
}

export default function geoAlbersUsaPr() {
  let cache: GeoStream | null,
    cacheStream: GeoStream | null,
    lower48 = geoAlbers(),
    lower48Point: GeoStream,
    alaska = geoConicEqualArea().rotate([154, 0]).center([-2, 58.5]).parallels([55, 65]),
    alaskaPoint: GeoStream,
    hawaii = geoConicEqualArea().rotate([157, 0]).center([-3, 19.9]).parallels([8, 18]),
    hawaiiPoint: GeoStream,
    puertoRico = geoConicEqualArea().rotate([66, 0]).center([0, 18]).parallels([8, 18]),
    puertoRicoPoint: GeoStream,
    point: [number, number] | null;

  const pointStream = { point(x: number, y: number) { point = [x, y]; } } as GeoStream;

  function projection(coordinates: [number, number]): [number, number] | null {
    const [x, y] = coordinates;
    point = null;
    lower48Point.point(x, y);
    if (point) return point;
    alaskaPoint.point(x, y);
    if (point) return point;
    hawaiiPoint.point(x, y);
    if (point) return point;
    puertoRicoPoint.point(x, y);
    return point;
  }

  projection.invert = function (coordinates: [number, number]): [number, number] | null {
    const k = lower48.scale(),
      t = lower48.translate(),
      x = (coordinates[0] - t[0]) / k,
      y = (coordinates[1] - t[1]) / k;

    const proj =
      (y >= 0.12 && y < 0.234 && x >= -0.425 && x < -0.214) ? alaska
      : (y >= 0.166 && y < 0.234 && x >= -0.214 && x < -0.115) ? hawaii
      : (y >= 0.15 && y < 0.238 && x >= 0.21 && x < 0.455) ? puertoRico
      : lower48;

    return proj.invert!(coordinates);
  };

  projection.stream = function (stream: GeoStream): GeoStream {
    if (cache && cacheStream === stream) return cache;
    cacheStream = stream;
    cache = multiplex([
      lower48.stream(stream),
      alaska.stream(stream),
      hawaii.stream(stream),
      puertoRico.stream(stream),
    ]);
    return cache;
  };

  projection.precision = function (_?: number) {
    if (_ === undefined) return lower48.precision();
    lower48.precision(_);
    alaska.precision(_);
    hawaii.precision(_);
    puertoRico.precision(_);
    return reset();
  };

  projection.scale = function (_?: number): any {
    if (_ === undefined) return lower48.scale();
    lower48.scale(_);
    alaska.scale(_ * 0.35);
    hawaii.scale(_);
    puertoRico.scale(_ * 1.5);
    return projection.translate(lower48.translate());
  };

  projection.translate = function (_?: [number, number]): any {
    if (_ === undefined) return lower48.translate();
    const k = lower48.scale(),
      x = +_[0],
      y = +_[1];

    lower48Point = lower48.translate(_)
      .clipExtent([[x - 0.455 * k, y - 0.238 * k], [x + 0.455 * k, y + 0.238 * k]])
      .stream(pointStream);

    alaskaPoint = alaska.translate([x - 0.307 * k, y + 0.201 * k])
      .clipExtent([[x - 0.425 * k + epsilon, y + 0.12 * k + epsilon], [x - 0.214 * k - epsilon, y + 0.233 * k - epsilon]])
      .stream(pointStream);

    hawaiiPoint = hawaii.translate([x - 0.205 * k, y + 0.212 * k])
      .clipExtent([[x - 0.214 * k + epsilon, y + 0.166 * k + epsilon], [x - 0.115 * k - epsilon, y + 0.233 * k - epsilon]])
      .stream(pointStream);

    puertoRicoPoint = puertoRico.translate([x + 0.35 * k, y + 0.2 * k])
      .clipExtent([[x + 0.21 * k + epsilon, y + 0.15 * k + epsilon], [x + 0.455 * k - epsilon, y + 0.238 * k - epsilon]])
      .stream(pointStream);

    return reset();
  };

  function reset() {
    cache = cacheStream = null;
    return projection;
  }

  return projection.scale(1070);
}
